/**
 * Подготовка текста перед отправкой.
 *
 * Фильтрация контента — забота бэкенда: он канонизирует текст, отсекает
 * control-символы и выбирает authoritative `error_code`. Здесь остаётся
 * ровно два дела:
 *
 * 1. счётчики лимитов, чтобы показать их до отправки;
 * 2. отсечение непарных surrogate — единственная проверка, предписанная
 *    фронту явно: битый UTF-8 не должен уйти в Protobuf.
 */

export const TEXT_MAX_CODE_POINTS = 300;
export const TEXT_MAX_UTF8_BYTES = 1024;

/** Причины, по которым отправка блокируется локально. */
export const TextFault = {
  MalformedSurrogate: "MALFORMED_SURROGATE",
  TooLong: "MESSAGE_TOO_LONG",
  TooLarge: "MESSAGE_TOO_LARGE",
} as const;

export type TextFaultCode = (typeof TextFault)[keyof typeof TextFault];

const utf8Encoder = new TextEncoder();

export function countCodePoints(value: string): number {
  let count = 0;
  for (const _ of value) count += 1;
  return count;
}

export function countUtf8Bytes(value: string): number {
  return utf8Encoder.encode(value).length;
}

/**
 * Приводит текст к тому виду, в котором его посчитает бэкенд: CRLF в LF и
 * обрезка по краям. Нужно только чтобы счётчики совпадали с серверными
 * лимитами, содержимое сообщения при этом не меняется.
 */
export function canonicalizeText(raw: string | null | undefined): string {
  if (raw == null) return "";
  return raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}

/**
 * Непарный high/low surrogate. Отклоняем до Protobuf encoding, чтобы не
 * полагаться на replacement character в рантайме.
 */
function hasUnpairedSurrogate(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const unit = value.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(i + 1);
      if (Number.isNaN(next) || next < 0xdc00 || next > 0xdfff) return true;
      i += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return true;
    }
  }
  return false;
}

interface TextMetrics {
  /** Текст, который уйдёт в `SEND_CHAT_MESSAGE.text`. */
  canonical: string;
  codePoints: number;
  utf8Bytes: number;
}

export interface TextValidation extends TextMetrics {
  /** Есть ли что отправлять и проходит ли текст локальные ограничения. */
  canSend: boolean;
  /** Причина блокировки для подсказки под полем, либо null. */
  fault: TextFaultCode | null;
}

/** Оба лимита действуют одновременно, поэтому проверяются оба. */
const TEXT_RULES: ReadonlyArray<{
  fault: TextFaultCode;
  isViolated: (metrics: TextMetrics) => boolean;
}> = [
  {
    fault: TextFault.MalformedSurrogate,
    isViolated: ({ canonical }) => hasUnpairedSurrogate(canonical),
  },
  {
    fault: TextFault.TooLong,
    isViolated: ({ codePoints }) => codePoints > TEXT_MAX_CODE_POINTS,
  },
  {
    fault: TextFault.TooLarge,
    isViolated: ({ utf8Bytes }) => utf8Bytes > TEXT_MAX_UTF8_BYTES,
  },
];

export function validateText(raw: string | null | undefined): TextValidation {
  const canonical = canonicalizeText(raw);
  const metrics: TextMetrics = {
    canonical,
    codePoints: countCodePoints(canonical),
    utf8Bytes: countUtf8Bytes(canonical),
  };

  const fault =
    TEXT_RULES.find((rule) => rule.isViolated(metrics))?.fault ?? null;

  return {
    ...metrics,
    fault,
    // Пустой ввод — не ошибка, просто нечего отправлять: подсказку не даём.
    canSend: canonical.length > 0 && fault === null,
  };
}

/**
 * Новый opaque ID на каждую отправку. UUID заведомо проходит серверные
 * ограничения на ID, поэтому отдельная проверка не нужна.
 */
export function newClientMessageId(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
}
